import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Chip, 
  IconButton,
  Fade,
  Slide
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrophyIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import './AchievementUnlocked.css';

const RARITY_KEYS = {
  '传说': 'achievement_rarity_legend',
  '史诗': 'achievement_rarity_epic',
  '稀有': 'achievement_rarity_rare',
  '普通': 'achievement_rarity_common',
  '常见': 'achievement_rarity_basic',
  'Legendary': 'achievement_rarity_legend',
  'Epic': 'achievement_rarity_epic',
  'Rare': 'achievement_rarity_rare',
  'Common': 'achievement_rarity_common',
  'Basic': 'achievement_rarity_basic'
};

const AchievementUnlocked = ({ 
  achievements = [], 
  onClose, 
  open = true 
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showContent, setShowContent] = useState(false);

  // 稀有度配置
  const rarityConfig = {
    '传说': { color: '#ff6b6b', icon: '👑', glow: '#ff6b6b' },
    '史诗': { color: '#a855f7', icon: '💎', glow: '#a855f7' },
    '稀有': { color: '#3b82f6', icon: '⭐', glow: '#3b82f6' },
    '普通': { color: '#10b981', icon: '🌟', glow: '#10b981' },
    '常见': { color: '#6b7280', icon: '🏅', glow: '#6b7280' }
  };

  const currentAchievement = achievements[currentIndex];
  const rarity = rarityConfig[currentAchievement?.rarity] || rarityConfig['普通'];

  useEffect(() => {
    if (open && achievements.length > 0) {
      setShowContent(true);
      
      // 如果有多个成就，自动轮播
      if (achievements.length > 1) {
        const timer = setTimeout(() => {
          if (currentIndex < achievements.length - 1) {
            setCurrentIndex(currentIndex + 1);
          } else {
            // 所有成就展示完毕，延迟关闭
            setTimeout(() => {
              onClose?.();
            }, 2000);
          }
        }, 3000);
        
        return () => clearTimeout(timer);
      } else {
        // 单个成就，3秒后自动关闭
        const timer = setTimeout(() => {
          onClose?.();
        }, 3000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [open, currentIndex, achievements.length, onClose]);

  // 全屏展示期间禁用页面滚动
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open || !currentAchievement) return null;

  return (
    <Box className="achievement-overlay" onClick={onClose}>
      <Slide direction="up" in={showContent} timeout={600}>
        <Box className="achievement-container" onClick={(e) => e.stopPropagation()}>
          {/* 背景光效 */}
          <Box 
            className="achievement-glow" 
            sx={{ 
              boxShadow: `0 0 60px ${rarity.glow}, 0 0 100px ${rarity.glow}40`
            }}
          />

          {/* 主要内容 */}
          <Box className="achievement-content">
            {/* 关闭按钮 */}
            <IconButton 
              className="close-button"
              onClick={onClose}
              sx={{ 
                position: 'absolute',
                top: 16,
                right: 16,
                color: '#ffffff',
                zIndex: 10
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* 成就图标 */}
            <Box className="achievement-icon-container">
              <Box 
                className="achievement-icon-glow"
                sx={{ 
                  backgroundColor: rarity.color,
                  boxShadow: `0 0 40px ${rarity.color}`
                }}
              />
              <Typography className="achievement-icon" variant="h1">
                {rarity.icon}
              </Typography>
              <TrophyIcon 
                className="trophy-icon"
                sx={{ 
                  color: rarity.color,
                  fontSize: 80,
                  filter: `drop-shadow(0 0 10px ${rarity.color})`
                }}
              />
            </Box>

            {/* 成就信息 */}
            <Fade in={showContent} timeout={1000}>
              <Box className="achievement-info">
                <Typography 
                  variant="h4" 
                  className="achievement-title"
                  sx={{ color: '#ffffff' }}
                >
                  {t('achievement_unlocked')}
                </Typography>
                
                <Typography 
                  variant="h5" 
                  className="achievement-name"
                  sx={{ color: rarity.color }}
                >
                  {currentAchievement.name}
                </Typography>
                
                {currentAchievement.description && (
                  <Typography 
                    variant="body1" 
                    className="achievement-description"
                    sx={{ color: '#e8f4f8' }}
                  >
                    {currentAchievement.description}
                  </Typography>
                )}

                {/* 稀有度标签 */}
                <Chip
                  label={t(RARITY_KEYS[currentAchievement.rarity] || 'achievement_rarity_common')}
                  className="rarity-chip"
                  sx={{
                    backgroundColor: rarity.color,
                    color: '#ffffff',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    boxShadow: `0 0 15px ${rarity.color}60`
                  }}
                />

                {/* 解锁时间 */}
                {currentAchievement.timestamp && (
                  <Typography 
                    variant="caption" 
                    className="unlock-time"
                    sx={{ color: '#b0b0b0' }}
                  >
                    {t('achievement_unlock_time_full', { date: new Date(currentAchievement.timestamp).toLocaleString() })}
                  </Typography>
                )}
              </Box>
            </Fade>

            {/* 多成就指示器 */}
            {achievements.length > 1 && (
              <Box className="achievement-indicator">
                <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
                  {currentIndex + 1} / {achievements.length}
                </Typography>
                <Box className="indicator-dots">
                  {achievements.map((_, index) => (
                    <Box
                      key={index}
                      className={`indicator-dot ${index === currentIndex ? 'active' : ''}`}
                      sx={{
                        backgroundColor: index === currentIndex ? rarity.color : '#666'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* 装饰性星星 */}
            <Box className="decorative-stars">
              {[...Array(12)].map((_, i) => (
                <StarIcon
                  key={i}
                  className="decorative-star"
                  sx={{
                    position: 'absolute',
                    color: rarity.color,
                    fontSize: Math.random() * 16 + 12,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    opacity: 0.6
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Slide>
    </Box>
  );
};

export default AchievementUnlocked;